import React, { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { CHECK_IN_STATUSES } from 'shared/consts';
import { useGetResourceEnvironmentQuery, useSubmitTemperatureVoteMutation } from 'store/resource-environment/api';
import { TemperatureVoteOption } from 'store/resource-environment/types';
import { isDateInBetweenInclusive } from 'utils/date';
import { VoteAck, VoteAckSub, VoteChip, VoteChips, VoteContainer, VotePrompt } from './styles';

export const VOTE_COOLDOWN_MS = 15 * 60 * 1000;

interface TemperatureVoteProps {
  /** Resource identifier as the booking payload carries it (uuid or numeric id — helix accepts both since #10991). */
  resourceId: string;
  startAt?: Date | string;
  endAt?: Date | string;
  /** Raw check_in.status from the booking payload; only CONFIRMED grants early (pre-window) eligibility. */
  checkInStatus?: string;
  /** styled-components passthrough, so host surfaces (receipt panes) can position the box. */
  className?: string;
}

const OPTIONS: Array<{ value: TemperatureVoteOption; labelId: string }> = [
  { value: 'too_hot', labelId: 'environment_vote_too_hot' },
  { value: 'just_fine', labelId: 'environment_vote_just_fine' },
  { value: 'too_cold', labelId: 'environment_vote_too_cold' },
];

/**
 * One vote lifecycle, impossible states unrepresentable:
 * idle → submitting(vote) → sent(vote, until) — or cooldown(until) on a 429
 * (the server discarded the vote, so no "thanks" copy), or hidden on a
 * definitive 403/404. A transient failure (network, timeout, 5xx) returns to
 * idle so the user can simply tap again.
 */
type VotePhase =
  | { kind: 'idle' }
  | { kind: 'submitting'; vote: TemperatureVoteOption }
  | { kind: 'sent'; vote: TemperatureVoteOption; until: number }
  | { kind: 'cooldown'; until: number }
  | { kind: 'hidden' };

/**
 * The HTTP status of a FetchBaseQueryError. For FETCH_ERROR / TIMEOUT_ERROR /
 * PARSING_ERROR, `status` is a string and the real HTTP code (if any) lives in
 * `originalStatus` — a 429 with a non-JSON body arrives that way.
 */
const httpStatus = (error: unknown): number | undefined => {
  if (typeof error !== 'object' || error === null) {
    return undefined;
  }
  const { status, originalStatus } = error as { status?: number | string; originalStatus?: number };

  return typeof status === 'number' ? status : originalStatus;
};

const stopPropagation = (event: React.MouseEvent): void => event.stopPropagation();

const toTime = (value?: Date | string): number | null => {
  if (!value) {
    return null;
  }
  const time = new Date(value).getTime();

  return Number.isNaN(time) ? null : time;
};

/**
 * Mirrors the server's vote gate (helix resource-environment.service.ts): the
 * booking window is active right now, or a CONFIRMED early check-in before the
 * window — never after the booking has ended, and never for open, canceled, or
 * auto-canceled (no-show) check-ins.
 */
export const isVoteEligible = (
  now: number,
  startAt?: Date | string,
  endAt?: Date | string,
  checkInStatus?: string,
): boolean => {
  const start = toTime(startAt);
  const end = toTime(endAt);
  if (start === null || end === null || now > end) {
    return false;
  }
  const inWindow = isDateInBetweenInclusive(new Date(now), new Date(start), new Date(end));
  const confirmedCheckIn = checkInStatus?.toLowerCase() === CHECK_IN_STATUSES.CONFIRMED;

  return inWindow || confirmedCheckIn;
};

/** The next moment the UI must re-evaluate: the window opens, the window ends, or a cooldown expires. */
const nextBoundary = (now: number, times: Array<number | null>): number | null => {
  const future = times.filter((time): time is number => time !== null && time > now);

  return future.length ? Math.min(...future) : null;
};

export const TemperatureVote = ({
  resourceId,
  startAt,
  endAt,
  checkInStatus,
  className,
}: TemperatureVoteProps): JSX.Element | null => {
  const intl = useIntl();
  const [phase, setPhase] = useState<VotePhase>({ kind: 'idle' });
  // Bumped by the boundary timer below — nothing else re-renders a mounted
  // booking card at 09:00 sharp, so eligibility and cooldown must self-tick.
  const [now, setNow] = useState(() => Date.now());

  const isActive = isVoteEligible(now, startAt, endAt, checkInStatus);
  const { currentData: environment } = useGetResourceEnvironmentQuery(
    { resourceUuid: resourceId },
    { skip: !isActive },
  );
  const [submitVote, { isLoading }] = useSubmitTemperatureVoteMutation();

  const phaseUntil = phase.kind === 'sent' || phase.kind === 'cooldown' ? phase.until : null;

  useEffect(() => {
    const current = Date.now();
    const boundary = nextBoundary(current, [toTime(startAt), toTime(endAt), phaseUntil]);
    if (boundary === null) {
      return undefined;
    }
    const timer = setTimeout(() => {
      setNow(Date.now());
      setPhase(previous =>
        (previous.kind === 'sent' || previous.kind === 'cooldown') && previous.until <= Date.now()
          ? { kind: 'idle' }
          : previous,
      );
    }, Math.min(boundary - current + 50, 0x7fffffff));

    return () => clearTimeout(timer);
  }, [startAt, endAt, phaseUntil, now]);

  const handleChipClick = useCallback(
    async (event: React.MouseEvent<HTMLButtonElement>) => {
      // The card itself navigates on click — a vote is not a navigation.
      event.stopPropagation();
      const vote = event.currentTarget.dataset.vote as TemperatureVoteOption;

      setPhase({ kind: 'submitting', vote });
      try {
        await submitVote({ resourceId, vote }).unwrap();
        setPhase({ kind: 'sent', vote, until: Date.now() + VOTE_COOLDOWN_MS });
      } catch (error) {
        const status = httpStatus(error);
        if (status === 429) {
          // The server discarded this vote (a recent vote elsewhere counts) —
          // honest copy: only "vote again in 15 minutes", never "thanks".
          // Deliberately conservative: the 429 carries no Retry-After, so the
          // full window restarts client-side even if the server's cooldown is
          // partly elapsed. Over-locking beats offering chips that 429 again;
          // surfacing the remaining time needs a helix contract change.
          setPhase({ kind: 'cooldown', until: Date.now() + VOTE_COOLDOWN_MS });
        } else if (status === 403 || status === 404) {
          // Definitive: not entitled, unmapped, or feature off — no vote UI.
          setPhase({ kind: 'hidden' });
        } else {
          // Transient (network, timeout, 5xx): let the user simply tap again.
          setPhase({ kind: 'idle' });
        }
      }
    },
    [resourceId, submitVote],
  );

  if (!isActive || phase.kind === 'hidden' || !environment?.available) {
    return null;
  }

  const selectedVote = phase.kind === 'submitting' || phase.kind === 'sent' ? phase.vote : null;
  const locked = phase.kind !== 'idle' || isLoading;

  return (
    <VoteContainer className={className} data-testid="temperature-vote" onClick={stopPropagation}>
      <VotePrompt>{intl.formatMessage({ id: 'environment_vote_prompt' })}</VotePrompt>
      <VoteChips>
        {OPTIONS.map(({ value, labelId }) => (
          <VoteChip
            key={value}
            type="button"
            data-testid={`temperature-vote-${value}`}
            data-vote={value}
            aria-pressed={selectedVote === value}
            selected={selectedVote === value}
            dimmed={locked && selectedVote !== value}
            disabled={locked}
            onClick={handleChipClick}
          >
            {intl.formatMessage({ id: labelId })}
          </VoteChip>
        ))}
      </VoteChips>
      {phase.kind === 'sent' && (
        <VoteAck data-testid="temperature-vote-ack">{intl.formatMessage({ id: 'environment_vote_thanks' })}</VoteAck>
      )}
      {(phase.kind === 'sent' || phase.kind === 'cooldown') && (
        <VoteAckSub data-testid="temperature-vote-cooldown">
          {intl.formatMessage({ id: 'environment_vote_cooldown' })}
        </VoteAckSub>
      )}
    </VoteContainer>
  );
};
